import React from 'react'
import {Box, Grid, Typography} from '@mui/material'
import {Link} from 'react-router-dom'
import withLayout from '../../hocs/Layout'
import PageTitle from '../../common/PageTitle'
import {
  PersonOutlineOutlined, LocationOnOutlined, LocalShippingOutlined,
  NotificationsOutlined, PeopleOutlined, CodeOutlined, EmailOutlined,
  ChatOutlined, MonetizationOnOutlined, Inventory2Outlined,
  ArrowForwardOutlined, PaletteOutlined, LanguageOutlined,
} from '@mui/icons-material'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, TEAL} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'
import {pageContainerSx,  sectionTitleSx} from '../../styles/patterns'

// ── CTA card — HomePage featureCard pattern ──
const ctaCardSx = {
  bgcolor: WHITE, borderRadius: '10px', p: '14px 16px',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  cursor: 'pointer', transition: 'all 0.2s', display: 'flex',
  alignItems: 'flex-start', gap: '12px', height: '100%',
  textDecoration: 'none',
  '&:hover': {transform: 'scale(1.01)', boxShadow: SHADOW.cardHover},
}
const badgeSx = (color) => ({
  width: 24, height: 24, borderRadius: '50%', bgcolor: color, color: WHITE,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: WEIGHT.bold, flexShrink: 0, mt: 0.2,
})
const iconBoxSx = (color) => ({
  width: 38, height: 38, borderRadius: '10px', bgcolor: `${color}12`,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color, flexShrink: 0, '& svg': {fontSize: 20},
})

// ── Settings data — numbered ──
const SECTIONS = [
  {
    num: 1, title: 'Account', accent: PRIMARY_BLUE,
    items: [
      {icon: <PersonOutlineOutlined />, title: 'Company Profile', desc: 'Business details, contact info & merchant type', path: '/profile'},
      {icon: <PeopleOutlined />, title: 'Manage Users', desc: 'Add, edit, or remove team members with role-based access', path: '/merchant-step3'},
    ],
  },
  {
    num: 2, title: 'Locations', accent: '#283593',
    items: [
      {icon: <LocationOnOutlined />, title: 'Pickup Locations', desc: 'Register D2C and PTL freight pickup addresses', path: '/merchant-step2'},
      {icon: <LocalShippingOutlined />, title: 'Delivery Locations', desc: 'Manage delivery address master for B2B shipments', path: '/delivery-location'},
      {icon: <LanguageOutlined />, title: 'Setup Pickup Drop Mapping', desc: 'Which warehouse ships to which state or pincode, used to quote delivery dates on your store', path: '/pickup-drop-mapping'},
    ],
  },
  {
    num: 3, title: 'Notifications & Communications', accent: '#E65100',
    items: [
      {icon: <NotificationsOutlined />, title: 'Customer Notifications', desc: 'Configure SMS, email & WhatsApp alerts', path: '/notification'},
      {icon: <EmailOutlined />, title: 'Automated Email Setup', desc: 'Transactional email templates and triggers', path: '/email-config'},
      {icon: <ChatOutlined />, title: 'Value Added Services', desc: 'WhatsApp, SMS & push notification channels', path: '/communication-config'},
    ],
  },
  {
    // Brand Setup — merchant-wide settings that apply whoever carries the shipment, so they
    // belong to Settings rather than to a courier plan. Customer Notifications deliberately
    // stays in section 3 where it already lived; it is not repeated here.
    num: 4, title: 'Brand Setup', accent: TEAL,
    items: [
      {icon: <PaletteOutlined />, title: 'Tracking Page', desc: 'Your branding, your URL, and the products buyers see while they wait', path: '/tracking-config'},
      /* DIY — hidden on prod (routes are flag-gated, not live). Uncomment when DIY goes live:
      {icon: <LabelOutlined />, title: 'Label Configuration', desc: 'Label size, and what is printed on the shipping label', path: '/label-config'},
      {icon: <AssignmentReturnOutlined />, title: 'Returns & QC', desc: 'Doorstep checks, return window and when refunds fire', path: '/returns-qc'},
      */
    ],
  },
  /* DIY — Couriers Setup & Manage hidden on prod (routes are flag-gated, not live). Uncomment when DIY goes live:
  {
    num: 5, title: 'Couriers Setup and Manage', accent: PURPLE,
    items: [
      {icon: <PowerSettingsNewOutlined />, title: 'Manage Couriers', desc: 'Enable couriers and block pincodes at courier-code level', path: '/manage-couriers'},
      {icon: <LowPriorityOutlined />, title: 'Courier Priority', desc: 'One allocation preference for every shipment, without writing a rule', path: '/courier-priority'},
      {icon: <RuleOutlined />, title: 'Courier Rules', desc: 'Conditions and courier priority per rule', path: '/courier-rules'},
      {icon: <AutoAwesomeOutlined />, title: 'PACE Engine', desc: 'What automatic allocation optimises for', path: '/pace-engine'},
    ],
  },
  */
  {
    num: 5, title: 'Integrations & Configuration', accent: '#637381',
    items: [
      {icon: <CodeOutlined />, title: 'Webhooks & API', desc: 'Configured webhooks and API documentation', path: '/view-webhook'},
      {icon: <MonetizationOnOutlined />, title: 'COD On Hold', desc: 'COD hold rules for high-risk orders', path: '/cod-on-hold-config'},
      {icon: <Inventory2Outlined />, title: 'Package Master', desc: 'Pre-define package dimensions & weights', path: '/setting/package'},
      /* DIY — hidden on prod (route flag-gated, not live). Uncomment when DIY goes live:
      {icon: <QrCode2Outlined />, title: 'Product Master', desc: 'Your SKU catalogue — weights, dimensions, HSN and images', path: '/dp/product-master'},
      */
      {icon: <LanguageOutlined />, title: 'Tracking Page', desc: 'Customize your white-label order tracking page', path: '/tracking-config'},
    ],
  },
]

const Setting = () => (
  <Box sx={pageContainerSx}>
    <PageTitle subTitle="Manage your account, locations, notifications, integrations, and system preferences">Settings & Configuration</PageTitle>


    {SECTIONS.map((sec) => (
      <Box key={sec.title} sx={{mb: 3}}>
        {/* Numbered section header */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2}}>
          <Box sx={badgeSx(sec.accent)}>{sec.num}</Box>
          <Typography sx={{...sectionTitleSx, fontSize: SIZE.lg, mb: 0}}>{sec.title}</Typography>
        </Box>

        <Grid container spacing={1.5}>
          {sec.items.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.path}>
              <Box component={Link} to={item.path} sx={ctaCardSx}>
                <Box sx={{width: 3, borderRadius: 3, alignSelf: 'stretch', flexShrink: 0, bgcolor: sec.accent}} />
                <Box sx={iconBoxSx(sec.accent)}>{item.icon}</Box>
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Typography sx={{fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY, mb: '2px', fontFamily: FONT}}>{item.title}</Typography>
                  <Typography sx={{fontSize: '10.5px', color: SUBTLE, lineHeight: 1.35, fontFamily: FONT}}>{item.desc}</Typography>
                  <Typography sx={{fontSize: '10.5px', color: PRIMARY_BLUE, fontWeight: WEIGHT.semibold, mt: 0.5, fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 0.3}}>
                    Explore <ArrowForwardOutlined sx={{fontSize: 12}} />
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    ))}
  </Box>
)

export default withLayout(Setting)
